import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';

import log from 'client/lib/log';
import withUtilityStripe from 'client/helpers/withUtilityStripe';
import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

const debug = log.debug('utilityProfit:components:UPStripeBankAccountForm');

const formFields = [
  {
    id: 'accountHolderName',
    title: 'Account Holder Name',
    type: 'short_text',
    validations: ['required'],
    properties: {
      placeholder: 'Write here...',
    },
  },
  {
    id: 'routingNumber',
    title: 'Routing Number',
    type: 'short_text',
    validations: ['required'],
    properties: {
      placeholder: '110000000',
      pattern: '[0-9]*',
    },
  },
  {
    id: 'accountNumber',
    title: 'Account Number',
    subtitle: 'Must be a checking account.',
    type: 'short_text',
    validations: ['required'],
    properties: {
      placeholder: '000123456789',
      pattern: '[0-9]*',
    },
  },
  {
    id: 'confirmAccountNumber',
    title: 'Confirm Account Number',
    type: 'short_text',
    validations: ['required', 'sameValue:accountNumber'],
    properties: {
      placeholder: '000123456789',
      pattern: '[0-9]*',
    },
  },
];

@withUtilityStripe
class UPStripeBankAccountForm extends Component {
  static propTypes = {
    disabled: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    submitErrorMessage: PropTypes.string,
    isSubmitting: PropTypes.bool,
    stripe: PropTypes.object.isRequired,
    buttonTitle: PropTypes.string,
  };

  static defaultProps = {
    buttonTitle: 'checkout',
  };

  constructor(props) {
    super(props);
    this.state = {
      isCreatingStripeSource: false,
      createStripeSourceError: null,
    };
    this._isUnmounting = false;
  }

  componentWillUnmount() {
    this._isUnmounting = true;
  }

  _onSubmit = (formValues) => {
    const { onSubmit, stripe } = this.props;
    debug('Creating Stripe Bank Account Token.', formValues);

    this.setState({
      isCreatingStripeSource: true,
      createStripeSourceError: null,
    });

    stripe
      .createToken('bank_account', {
        country: 'US',
        currency: 'usd',
        routing_number: formValues.routingNumber,
        account_number: formValues.accountNumber,
        account_holder_name: formValues.accountHolderName,
        account_holder_type: 'individual',
      })
      .then((result) => {
        let error = result.error;

        if (!this._isUnmounting) {
          this.setState({
            isCreatingStripeSource: false,
            createStripeSourceError: null,
          });
        }
        if (error) {
          throw error;
        }

        if (!result.token) {
          error = new Error('stripe create bank account token: No response.');
          error.name = 'UPStripeCreateSourceOrTokenNoResponseError';
          throw error;
        }

        debug('Stripe bank account token received.', result.token);
        onSubmit(result.token, formValues);
      })
      .catch((error) => {
        log.error(error);
        this.setState({
          isCreatingStripeSource: false,
          createStripeSourceError: error,
        });
      });
  };

  render() {
    const { disabled, isSubmitting, submitErrorMessage, buttonTitle } = this.props;
    const { isCreatingStripeSource, createStripeSourceError } = this.state;
    const isBusy = !!(isSubmitting || isCreatingStripeSource);

    debug({
      submitErrorMessage,
      createStripeSourceError,
    });

    const errorMessage = createStripeSourceError
      ? createStripeSourceError.message || JSON.stringify(createStripeSourceError)
      : submitErrorMessage || null;

    return (
      <Formik
        onSubmit={this._onSubmit}
        initialValues={createInitialValues({ formFields })}
        validate={createFormValidationFn(formFields)}
        render={(formikBag) => {
          return (
            <div>
              {formFields.map((formField, index) => (
                <SrFormikField
                  formField={formField}
                  formikBag={formikBag}
                  theme={SR_FIELD_THEME_ADOBE}
                  margin={index === 0 ? '0' : null}
                  key={formField.id}
                />
              ))}
              {errorMessage && <SrErrorMessage>{errorMessage}</SrErrorMessage>}
              <SrBox paddingTop="20px">
                <SrButton
                  disabled={disabled || isBusy || !formikBag.isValid}
                  type="button"
                  onClick={formikBag.handleSubmit}
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                >
                  {isBusy ? 'loading...' : buttonTitle}
                </SrButton>
              </SrBox>
              <span className="helperTextContainer">
                <span className="helperText">
                  <span role="img" aria-label="lock">
                    {' '}
                    🔒
                  </span>{' '}
                  Securely encrypted payment
                </span>
              </span>
            </div>
          );
        }}
      />
    );
  }
}

UPStripeBankAccountForm.displayName = 'UPStripeBankAccountForm';

export default UPStripeBankAccountForm;
